import { useCallback, useMemo } from "react";
import {
  createNormalizedError,
  normalizeGraphQLError,
  type INormalizedError,
} from "../errors";

/** Normalized query state returned by every rewards query hook. */
export interface IQueryState<TData> {
  /** `undefined` while loading / skipped / errored without data. */
  data: TData | undefined;
  isLoading: boolean;
  error: INormalizedError | null;
  /** Re-runs the query. Referentially stable across renders. */
  refetch: () => void;
}

/** Query state whose `data` falls back to a default (e.g. `[]`) while not loaded. */
export type IQueryStateWithDefault<TData> = Omit<IQueryState<TData>, "data"> & { data: TData };

/** Minimal structural shape of an Apollo query result. */
export interface ApolloQueryLike<TData> {
  data?: TData;
  loading: boolean;
  error?: unknown;
  refetch: () => unknown;
}

/**
 * Maps an Apollo query result into {@link IQueryState}. Pure (no memoization):
 * hooks use {@link useQueryState}, which adds stable references.
 */
export function mapQueryState<TData>(result: ApolloQueryLike<TData>): IQueryState<TData> {
  return {
    data: result.data,
    isLoading: result.loading,
    error: result.error ? normalizeGraphQLError(result.error) : null,
    refetch: () => {
      void result.refetch();
    },
  };
}

/**
 * Hook form of {@link mapQueryState}: `select` projects the operation data
 * (it is only called when data exists and must depend on `data` alone); the
 * normalized error, the selected data and `refetch` keep stable references.
 */
export function useQueryState<TData, TSelected>(
  result: ApolloQueryLike<TData>,
  select: (data: TData) => TSelected | undefined,
): IQueryState<TSelected>;
export function useQueryState<TData, TSelected>(
  result: ApolloQueryLike<TData>,
  select: (data: TData) => TSelected,
  fallback: TSelected,
): IQueryStateWithDefault<TSelected>;
export function useQueryState<TData, TSelected>(
  result: ApolloQueryLike<TData>,
  select: (data: TData) => TSelected | undefined,
  fallback?: TSelected,
): IQueryState<TSelected> {
  const { data, loading, error, refetch: apolloRefetch } = result;

  // `select` is deliberately not a dependency: it must be a pure projection of `data`.
  const selected = useMemo(() => (data === undefined ? fallback : select(data)), [data, fallback]);
  const normalizedError = useMemo(() => (error ? normalizeGraphQLError(error) : null), [error]);
  const refetch = useCallback(() => {
    void apolloRefetch();
  }, [apolloRefetch]);

  return useMemo(
    () => ({ data: selected, isLoading: loading, error: normalizedError, refetch }),
    [selected, loading, normalizedError, refetch],
  );
}

/** Normalized mutation state returned by every rewards mutation hook. */
export interface IMutationState<TData, TVariables> {
  /**
   * Executes the mutation; resolves with data or rejects with an
   * {@link INormalizedError}. Referentially stable across renders.
   */
  mutate: (variables: TVariables) => Promise<TData>;
  isLoading: boolean;
  error: INormalizedError | null;
  data: TData | undefined;
}

type MutationRunner<TData, TVariables> = (options: {
  variables: TVariables;
}) => Promise<{ data?: TData | null; error?: unknown }>;

/** Minimal structural shape of an Apollo `useMutation` tuple. */
export interface ApolloMutationLike<TData, TVariables> {
  0: MutationRunner<TData, TVariables>;
  1: { data?: TData | null; loading: boolean; error?: unknown };
}

/**
 * Wraps an Apollo mutation runner so it resolves with `data` or rejects with a
 * single {@link INormalizedError} (never a raw Apollo error, never normalized twice).
 */
export function createMutate<TData, TVariables>(
  run: MutationRunner<TData, TVariables>,
): (variables: TVariables) => Promise<TData> {
  return async (variables: TVariables): Promise<TData> => {
    let result: { data?: TData | null; error?: unknown };
    try {
      result = await run({ variables });
    } catch (error) {
      throw normalizeGraphQLError(error);
    }
    if (result.error) throw normalizeGraphQLError(result.error);
    if (!result.data) throw createNormalizedError("Unknown", "empty_response");
    return result.data;
  };
}

/** Maps an Apollo `useMutation` tuple into {@link IMutationState}. Pure (no memoization). */
export function mapMutationState<TData, TVariables>(
  tuple: ApolloMutationLike<TData, TVariables>,
): IMutationState<TData, TVariables> {
  const state = tuple[1];
  return {
    mutate: createMutate(tuple[0]),
    isLoading: state.loading,
    error: state.error ? normalizeGraphQLError(state.error) : null,
    data: state.data ?? undefined,
  };
}

/** Hook form of {@link mapMutationState} with a stable `mutate` and memoized error. */
export function useMutationState<TData, TVariables>(
  tuple: ApolloMutationLike<TData, TVariables>,
): IMutationState<TData, TVariables> {
  const run = tuple[0];
  const { data, loading, error } = tuple[1];
  const mutate = useMemo(() => createMutate(run), [run]);
  const normalizedError = useMemo(() => (error ? normalizeGraphQLError(error) : null), [error]);
  return useMemo(
    () => ({ mutate, isLoading: loading, error: normalizedError, data: data ?? undefined }),
    [mutate, loading, normalizedError, data],
  );
}
